/**
 * InvoicesSummaryWidget — matches the Penpot mockup: 2×2 grid of 4 tiles
 * (ALL OPEN, PARTIALLY PAID, PAID, OVER PAID), each showing big count +
 * sub $ total + colored bottom accent + uppercase label.
 *
 * Tile order/labels are fixed (not driven by the catalog) because the FE
 * needs to display the OVER PAID tile that the BE derives outside the
 * regular status_id pipeline. Colors are hardcoded to match the Penpot
 * palette — these are dashboard-specific brand colors, not from public.status.
 */

import * as React from "react";
import { useQueryClient } from "react-query";
import { MetricTile, RefreshButton, TileGrid, WidgetCard } from "components/dashboard";
import { useInvoicesSummary, formatQueryError } from "../hooks";
import type { LayoutItem, WidgetConfig } from "../types";

interface Props {
  widget: WidgetConfig;
  layout: LayoutItem;
}

// Status IDs from public.status WHERE type='invoice'
const STATUS_OPEN = 17;
const STATUS_PARTIALLY_PAID = 38;
const STATUS_PAID = 18;

// Penpot accent colors (R,G,B for formatRgb consistency with other widgets)
const COLOR_OPEN = "139,92,246";       // #8b5cf6 violet-500
const COLOR_PARTIAL = "245,158,11";    // #f59e0b amber-500
const COLOR_PAID = "34,197,94";        // #22c55e green-500
const COLOR_OVER_PAID = "59,130,246";  // #3b82f6 blue-500

export function InvoicesSummaryWidget({ widget, layout }: Props) {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useInvoicesSummary();

  const bucket = (id: number) => data?.statusBuckets.find((b) => b.statusId === id);
  const open = bucket(STATUS_OPEN);
  const partial = bucket(STATUS_PARTIALLY_PAID);
  const paid = bucket(STATUS_PAID);
  const overPaid = data?.overPaid;

  return (
    <WidgetCard
      title={widget.title}
      w={widget.defaultSize.w}
      h={widget.defaultSize.h}
      x={layout.x}
      y={layout.y}
      loading={isLoading}
      error={error ? formatQueryError(error) : null}
      headerRight={
        <RefreshButton onClick={() => queryClient.invalidateQueries(["dashboard", "invoices-summary"])} />
      }
    >
      <TileGrid cols={2}>
        <MetricTile
          label="All Open"
          value={open?.count ?? 0}
          subAmountCents={open?.amountCents ?? 0}
          color={COLOR_OPEN}
          loading={isLoading}
        />
        <MetricTile
          label="Partially Paid"
          value={partial?.count ?? 0}
          subAmountCents={partial?.amountCents ?? 0}
          color={COLOR_PARTIAL}
          loading={isLoading}
        />
        <MetricTile
          label="Paid"
          value={paid?.count ?? 0}
          subAmountCents={paid?.amountCents ?? 0}
          color={COLOR_PAID}
          loading={isLoading}
        />
        <MetricTile
          label="Over Paid"
          value={overPaid?.count ?? 0}
          subAmountCents={overPaid?.amountCents ?? 0}
          color={COLOR_OVER_PAID}
          loading={isLoading}
        />
      </TileGrid>
    </WidgetCard>
  );
}
